<template>
    <div class="flex flex-col gap-4">
        <!--header-->
        <div class="flex justify-between">
            <div class="align-self-center">
                <h3 class="page-title">
                    <template v-if="showPayPropUploadForm">
                        PayProp File Imports
                    </template>

                    <template v-else>
                        {{ searchParams.archived_filter ? "Archived" : "" }} Assets
                    </template>
                </h3>
            </div>

            <div class="flex gap-2">
                <template v-if="!showPayPropUploadForm">
                    <div v-if="hasPaypropFilesIntegration" class="">
                        <button class="btn btn-primary whitespace-nowrap" @click="showPayPropUploadForm = true">
                            Import PayProp Data
                        </button>
                    </div>

                    <div class="btn-group pull-right">
                        <div v-if="verifyTeamIsSubscribed()" class="dropdown drop-left">
                            <button
                                class="btn btn-default dropdown-toggle"
                                type="button"
                                id="dropdownMenuButton"
                                data-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false">
                                Options
                            </button>
                            <div class="dropdown-menu dropdown-menu-right" aria-labelledby="dropdownMenuButton">
                                <a class="dropdown-item" @click="toggleArchivedAssets">
                                    View {{ searchParams.archived_filter ? "Current" : "Archived" }}
                                </a>
                                <a class="dropdown-item" @click="toggleMergeOption">
                                    {{ showMergeOption ? "Hide Merge Option" : "Merge Assets" }}
                                </a>
                                <a
                                    v-if="app.state.user.support"
                                    @click.prevent="showImportFileUploadForm"
                                    class="dropdown-item"
                                    href="#">
                                    Import {{ app.lang.assetsName }}
                                </a>
                                <a class="dropdown-item" @click="exportAssets">
                                    Export {{ app.lang.assetsName }}
                                </a>
                            </div>
                        </div>
                    </div>
                </template>
                <template v-else>
                    <div class="pull-right">
                        <button class="btn btn-default" @click="showPayPropUploadForm = false">
                            Back
                        </button>
                    </div>
                </template>
            </div>
        </div>

        <!--Asset List-->
        <div v-show="!showPayPropUploadForm">
            <div class="card">
                <div class="card-body">
                    <!--Filters-->
                    <div class="flex items-center gap-4 justify-start md:justify-between flex-wrap">
                        <div class="flex flex-wrap items-center justify-start gap-2">
                            <!--Portfolios Filter-->
                            <el-select placeholder="All Portfolios" @change="doFilter" v-model="searchParams.portfolio_ids_filter" multiple filterable clearable
                                       class="w-48">
                                <el-option
                                    v-for="portfolio in portfolioFilterOptions"
                                    :key="portfolio.id + portfolio.name"
                                    :label="portfolio.name | titlecase"
                                    :value="portfolio.id">
                                </el-option>
                            </el-select>

                            <!--Search Asset Groups Dropdown-->
                            <div>
                                <remote-select
                                    class="w-52"
                                    @focus="searchAssetGroups"
                                    :remote-method="searchAssetGroups"
                                    @input="setAssetGroupFilter"
                                    :data="assetGroups"
                                    :loading="loading"
                                    item-label="name"
                                    item-key="id"
                                    placeholder="Search Asset Groups">
                                    <template slot-scope="props">
                                        {{ props.item.name }}
                                    </template>
                                </remote-select>
                            </div>

                            <!--Search Assets Text Field-->
                            <div>
                                <input
                                    type="text"
                                    v-model="searchParams.filter_text"
                                    @input="doFilter"
                                    :placeholder="'Search Assets'"
                                    class="form-control w-52"></input>
                            </div>

                            <!--Mandate Type Filter-->
                            <div class="">
                                <button
                                    type="button"
                                    class="btn btn-primary dropdown-toggle"
                                    data-toggle="dropdown"
                                    aria-haspopup="true"
                                    aria-expanded="false">
                                    {{ currentMandateFilter | titlecase }}
                                </button>

                                <div class="dropdown-menu">
                                    <a
                                        v-for="mandateType in mandateTypes"
                                        class="dropdown-item"
                                        @click="selectMandateTypeFilter(mandateType)">
                                        {{ mandateType | titlecase }}
                                    </a>
                                    <div class="dropdown-divider"></div>
                                    <a class="dropdown-item" @click="selectMandateTypeFilter('all mandates')">All Mandates</a>
                                </div>
                            </div>

                            <!--Inspection Filters Dropdown-->
                            <el-popover
                                v-if="userCan('manage-inspections')"
                                v-click-outside="hideInspectionFilters"
                                placement="bottom"
                                :width="notSmallScreen ? '380' : '310'"
                                v-model="showInspectionFilters"
                                trigger="manual">

                                <button slot="reference" class="btn btn-outline-primary hide-while-loading"
                                        @click.prevent="toggleInspectionFilters()">
                                    <i class="mdi mdi-filter-outline"></i>Inspection Filters
                                </button>

                                <div>
                                    <div class="form-group">
                                        <label>To-do Date Range</label>
                                        <el-date-picker
                                            v-model="searchParams.todo_date_range_filter"
                                            value-format="yyyy-MM-dd"
                                            type="daterange"
                                            range-separator="-"
                                            start-placeholder="From date"
                                            end-placeholder="To date">
                                        </el-date-picker>
                                    </div>

                                    <div class="form-group">
                                        <label>Has Inspection With Type</label>
                                        <el-select v-model="searchParams.inspection_type_filter" class="w-100" multiple>
                                            <el-option
                                                v-for="type in inspectionTypes"
                                                :key="type"
                                                :label="type | capitalize"
                                                :value="type">
                                            </el-option>
                                        </el-select>
                                    </div>

                                    <div class="form-group">
                                        <simple-dropdown :label="reminderDateFilterLabel">
                                            <template slot="buttonOptions">
                                                <simple-dropdown-option @click="filterReminderDate('all')" value="All">
                                                    All
                                                </simple-dropdown-option>

                                                <simple-dropdown-option @click="filterReminderDate('current')" value="Current">
                                                    Current
                                                </simple-dropdown-option>

                                                <simple-dropdown-option @click="filterReminderDate('upcoming')" value="Upcoming">
                                                    Upcoming
                                                </simple-dropdown-option>

                                                <simple-dropdown-option @click="filterReminderDate('none')" value="None">
                                                    None
                                                </simple-dropdown-option>
                                            </template>
                                        </simple-dropdown>
                                    </div>

                                    <div class="form-group">
                                        <simple-dropdown :label="lastInspectionFilterLabel">
                                            <template slot="buttonOptions">
                                                <simple-dropdown-option @click="filterLastInspectionDate('all')" value="All">
                                                    All
                                                </simple-dropdown-option>

                                                <simple-dropdown-option @click="filterLastInspectionDate('mt_3_months_ago')" value="More Than 3 Months Ago">
                                                    More Than 3 Months Ago
                                                </simple-dropdown-option>

                                                <simple-dropdown-option @click="filterLastInspectionDate('mt_6_months_ago')" value="More Than 6 Months Ago">
                                                    More Than 6 Months Ago
                                                </simple-dropdown-option>

                                                <simple-dropdown-option @click="filterLastInspectionDate('mt_12_months_ago')" value="More Than 12 Months Ago">
                                                    More Than 12 Months Ago
                                                </simple-dropdown-option>

                                                <simple-dropdown-option @click="filterLastInspectionDate('never')" value="Never">
                                                    Never
                                                </simple-dropdown-option>
                                            </template>
                                        </simple-dropdown>
                                    </div>

                                    <div class="form-group">
                                        <simple-dropdown :label="nextInspectionFilterLabel">
                                            <template slot="buttonOptions">
                                                <simple-dropdown-option @click="filterNextInspectionDate('all')" value="All">
                                                    All
                                                </simple-dropdown-option>

                                                <simple-dropdown-option @click="filterNextInspectionDate('lt_3_months')" value="In Less Than 3 Months">
                                                    In Less Than 3 Months
                                                </simple-dropdown-option>

                                                <simple-dropdown-option @click="filterNextInspectionDate('lt_6_months')" value="In Less Than 6 Months">
                                                    In Less Than 6 Months
                                                </simple-dropdown-option>

                                                <simple-dropdown-option @click="filterNextInspectionDate('lt_12_months')" value="In Less Than 12 Months">
                                                    In Less Than 12 Months
                                                </simple-dropdown-option>

                                                <simple-dropdown-option @click="filterNextInspectionDate('never')" value="Never">
                                                    Never
                                                </simple-dropdown-option>
                                            </template>
                                        </simple-dropdown>
                                    </div>

                                    <div class="form-group">
                                        <button class="btn btn-primary" @click="doFilter">Apply</button>
                                    </div>
                                </div>
                            </el-popover>

                            <!--Maintenance Filters Dropdown-->
                            <el-popover
                                v-if="userCan('manage-maintenance-tickets') && hasFeature('release_asset_maintenance_filters')"
                                v-click-outside="hideMaintenanceFilters"
                                placement="bottom"
                                :width="notSmallScreen ? '380' : '310'"
                                v-model="showMaintenanceFilters"
                                trigger="manual">

                                <button slot="reference" class="btn btn-outline-primary hide-while-loading"
                                        @click.prevent="toggleMaintenanceFilters()">
                                    <i class="mdi mdi-filter-outline"></i>Maintenance Filters
                                </button>

                                <div>
                                    <!--Maintenance Ticket Filter-->
                                    <div class="form-group mt-4">
                                        <label class="">Last Ticket Opened</label>
                                        <simple-dropdown :label="lastMaintenanceTicketFilter">
                                            <template slot="buttonOptions">
                                                <simple-dropdown-option @click="filterLastMaintenanceTicketDate('all')" value="All">
                                                    All
                                                </simple-dropdown-option>

                                                <simple-dropdown-option @click="filterLastMaintenanceTicketDate('mt_3_months_ago')"
                                                                        value="More Than 3 Months Ago">
                                                    More Than 3 Months Ago
                                                </simple-dropdown-option>

                                                <simple-dropdown-option @click="filterLastMaintenanceTicketDate('mt_6_months_ago')"
                                                                        value="More Than 6 Months Ago">
                                                    More Than 6 Months Ago
                                                </simple-dropdown-option>

                                                <simple-dropdown-option @click="filterLastMaintenanceTicketDate('mt_12_months_ago')"
                                                                        value="More Than 12 Months Ago">
                                                    More Than 12 Months Ago
                                                </simple-dropdown-option>

                                                <simple-dropdown-option @click="filterLastMaintenanceTicketDate('never')" value="Never">
                                                    Never
                                                </simple-dropdown-option>
                                            </template>
                                        </simple-dropdown>
                                    </div>

                                    <!--Maintenance Requests Filter-->
                                    <div class="form-group mt-4">
                                        <label class="">Last Maintenance Request Logged</label>
                                        <simple-dropdown :label="lastMaintenanceRequestFilter">
                                            <template slot="buttonOptions">
                                                <simple-dropdown-option @click="filterLastMaintenanceRequestDate('all')" value="All">
                                                    All
                                                </simple-dropdown-option>

                                                <simple-dropdown-option @click="filterLastMaintenanceRequestDate('mt_3_months_ago')"
                                                                        value="More Than 3 Months Ago">
                                                    More Than 3 Months Ago
                                                </simple-dropdown-option>

                                                <simple-dropdown-option @click="filterLastMaintenanceRequestDate('mt_6_months_ago')"
                                                                        value="More Than 6 Months Ago">
                                                    More Than 6 Months Ago
                                                </simple-dropdown-option>

                                                <simple-dropdown-option @click="filterLastMaintenanceRequestDate('mt_12_months_ago')"
                                                                        value="More Than 12 Months Ago">
                                                    More Than 12 Months Ago
                                                </simple-dropdown-option>

                                                <simple-dropdown-option @click="filterLastMaintenanceRequestDate('never')" value="Never">
                                                    Never
                                                </simple-dropdown-option>
                                            </template>
                                        </simple-dropdown>
                                    </div>

                                    <div class="form-group mt-4">
                                        <button class="btn btn-primary" @click="doFilter">Apply</button>
                                    </div>
                                </div>
                            </el-popover>
                        </div>

                        <div class="flex items-center gap-2">
                            <div v-if="showBulkActions" class="hide-while-loading">
                                <button
                                    type="button"
                                    class="btn btn-primary dropdown-toggle"
                                    data-toggle="dropdown"
                                    aria-haspopup="true"
                                    aria-expanded="false">
                                    Bulk Update
                                </button>
                                <div class="dropdown-menu">
                                    <a class="dropdown-item" @click="updatePortfolio">Portfolio</a>
                                    <a class="dropdown-item" @click="toggleAssetGroupsBulkUpdateModal">Asset Group</a>
                                    <div class="dropdown-divider"></div>
                                    <a class="dropdown-item" @click="showSetPrimaryContactModal">Set Primary Contact</a>
                                    <div class="dropdown-divider"></div>
                                    <a class="dropdown-item" v-if="!searchParams.archived_filter" @click="archiveAssets">Archive</a>
                                    <a class="dropdown-item" v-if="searchParams.archived_filter" @click="unarchiveSelectedAssets">Unarchive</a>
                                </div>
                            </div>
                            <button class="btn btn-primary hide-while-loading whitespace-nowrap" @click="toggleTodoModal"
                                    v-if="userCan('manage-inspections')">
                                New To-do
                            </button>

                            <button class="btn btn-primary hide-while-loading whitespace-nowrap" @click="showAssetForm">
                                New Asset
                            </button>
                        </div>
                    </div>

                    <div class="table-responsive mt-6" :class="{'table-loading' : isLoadingTableData}">
                        <vuetable ref="vuetable"
                                  api-url="/assets/all"
                                  :fields="columns"
                                  :css="css.table"
                                  pagination-path=""
                                  :append-params="searchParams"
                                  @vuetable:loading="onLoading"
                                  @vuetable:loaded="onLoaded"
                                  @vuetable:checkbox-toggled="checkboxToggled"
                                  @vuetable:checkbox-toggled-all="checkboxToggled"
                                  @vuetable:pagination-data="onPaginationData">

                            <template slot="asset_name" slot-scope="props">
                                <div class="flex items-center gap-x-2">
                                    <span>{{ props.rowData.name }}</span>

                                    <span v-if="props.rowData.external_source !== null"
                                          data-toggle="tooltip"
                                          :data-original-title="props.rowData.external_source !== 'replication' ? 'Linked to external data feed' : ''"
                                          class="mdi mdi-link-variant text-gray-400 text-xs mb-1">
                                        {{ props.rowData.external_source }}
                                    </span>
                                </div>
                            </template>

                            <template slot="landlord_full_name" slot-scope="props">
                                <div class="items-center gap-x-2">
                                    <div>{{ props.rowData.landlord.full_name }}</div>
                                    <div>
                                        <span v-if="props.rowData.current_mandate_type !== null"
                                              :class="props.rowData.current_mandate_type === 'managed' ? 'text-indigo-500': 'text-gray-500'">
                                            {{ props.rowData.current_mandate_type | titlecase }}
                                        </span>
                                    </div>
                                </div>
                            </template>

                            <template slot="last_inspection" slot-scope="props">
                                <a v-if="props.rowData.last_inspection" :href="'/inspections/' + props.rowData.last_inspection.id"
                                   class="flex flex-col text-indigo-600 hover:text-indigo-800 transition duration-300">
                                    <span>{{ props.rowData.last_inspection.type }}</span>
                                    <span>{{ props.rowData.last_inspection.date | localeUnawareDatetime }}</span>
                                </a>
                            </template>

                            <template slot="next_scheduled_inspection" slot-scope="props">
                                <a v-if="props.rowData.next_inspection" :href="'/inspections/' + props.rowData.next_inspection.id"
                                   class="flex flex-col transition duration-300"
                                   :class="props.rowData.next_inspection.status === 'scheduled' && new Date(props.rowData.next_inspection.date) < new Date() ?
                                           'text-red-600 hover:text-red-800' : 'text-indigo-600 hover:text-indigo-800'">
                                    <span>{{ props.rowData.next_inspection.type }}</span>
                                    <span>{{ props.rowData.next_inspection.date | localeUnawareDatetime }}</span>
                                </a>
                            </template>

                            <template slot="next_todo" slot-scope="props">
                                <button class="flex space-x-1 items-center font-medium hover:text-indigo-600 transition duration-300"
                                        v-if="props.rowData.next_todo && props.rowData.next_todo.latest_reminder"
                                        @click="editTodo(props.rowData.next_todo, props.rowData)">
                                    <icon-pencil-alt class="w-4 h-4"></icon-pencil-alt>

                                    <span class="whitespace-nowrap">
                                        {{ props.rowData.next_todo.latest_reminder.reminder_date | date }}
                                    </span>
                                </button>
                            </template>

                            <template slot="view_asset" slot-scope="props">
                                <div class="text-nowrap">
                                    <a v-if="showMergeOption" @click.prevent="onActionClick('merge-asset', props.rowData)"
                                       class="btn btn-rounded btn-danger btn-sm text-white">
                                        Merge
                                    </a>

                                    <a :href="'/assets/' + props.rowData.id"
                                       class="btn btn-rounded btn-outline-primary btn-sm">
                                        View
                                    </a>
                                </div>
                            </template>
                        </vuetable>
                    </div>

                    <div class="flex flex-wrap items-center justify-center md:justify-between gap-2">
                        <vuetable-pagination-info ref="paginationInfo"></vuetable-pagination-info>

                        <simple-dropdown :label="perPageLabel" v-if="userHasRole('Support')">
                            <template slot="buttonOptions">
                                <simple-dropdown-option v-for="perPage in [10, 25, 50, 100]" :key="perPage" @click="filterPerPage(perPage)" :value="perPage">
                                    {{ perPage }}
                                </simple-dropdown-option>
                            </template>
                        </simple-dropdown>

                        <div class="text-right">
                            <vuetable-pagination ref="pagination"
                                                 @vuetable-pagination:change-page="onChangePage"
                            ></vuetable-pagination>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!--Import-->
        <div id="asset-import-modal" class="modal fade" tabindex="-1" role="dialog"
             style="display: none;">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h4 class="modal-title font-normal" id="modalTitle">Upload Asset Import File</h4>
                        <button type="button" class="close" data-dismiss="modal" aria-hidden="true">×</button>
                    </div>

                    <div class="modal-body">
                        <el-upload
                            :action="assetImportUploadUrl"
                            :headers="assetImportUploadRequestHeaders"
                            :show-file-list="true"
                            :limit="1"
                            :on-success="handleImportUploadSuccess"
                            :on-error="handleImportUploadError">
                            <el-button type="primary">Select File</el-button>
                        </el-upload>
                    </div>

                    <div class="modal-footer">
                        <button type="button" class="btn btn-default waves-effect" data-dismiss="modal"
                                :disabled="importForm.busy">
                            Cancel
                        </button>

                        <button type="button" class="btn btn-success waves-effect" data-dismiss="modal"
                                :disabled="importForm.busy"
                                onClick="window.location.reload()">
                            Done
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div>
            <create-asset></create-asset>

            <payprop-files-upload
                v-if="showPayPropUploadForm"
                :has-queued-imports="hasQueuedImports"
            ></payprop-files-upload>

            <merge-property-modal
                :asset="selectedAsset"
                @asset-merged="hideMergeModal(); doFilter();"
            ></merge-property-modal>

            <el-dialog
                class="hide-while-loading"
                v-if="setPrimaryContactModalVisible"
                title="Set new primary contact"
                :close-on-click-modal="false"
                :visible.sync="setPrimaryContactModalVisible"
                width="500px"
                :before-close="closeSetPrimaryContactModal">
                <h4 slot="title">Set new primary contact on selected assets</h4>
                <div v-show="setPrimaryContactModalVisible">

                    <div class="form-group mb-4">
                        <label>Select Role</label>

                        <el-select
                            v-model="bulkSetContactForm.role"
                            class="col-sm-12" style="padding:0"
                            placeholder="Select Role">
                            <el-option value="landlord" label="Landlord" key="landlord"></el-option>
                            <el-option value="tenant" label="Tenant" key="tenant"></el-option>
                            <el-option value="other" label="Other" key="other"></el-option>
                        </el-select>

                        <span
                            class="invalid-feedback"
                            :class="{'d-block': bulkSetContactForm.errors.has('category')}"
                            v-show="bulkSetContactForm.errors.has('category')"
                        >{{ bulkSetContactForm.errors.get("category") }}</span>
                    </div>

                    <div class="form-group mb-4">
                        <label for="searchContact">Select Contact</label>
                        <div class="row" id="searchContact">
                            <div class="col-sm-12">
                                <el-select
                                    value-key="id"
                                    class="w-100"
                                    v-model="selectedContact"
                                    @focus="searchContacts('')"
                                    filterable
                                    remote
                                    :placeholder="'Search contact by Name / Email / Mobile'"
                                    :remote-method="searchContacts"
                                    :loading="loading">
                                    <el-option
                                        v-for="contact in suggestedContacts"
                                        :key="contact.id"
                                        :label="contact.full_name"
                                        :value="contact">
                                        <span class="position-relative">
                                            <span v-if="contact.pivot"
                                                  class="label label-light-primary mr-2">
                                                {{ contact.pivot.role }}</span>
                                            <span v-if="contact.juristic_name" class="mr-1">
                                                {{ contact.juristic_name }} <span class="text-info">({{ contact.full_name }})</span></span>
                                            <span v-else>{{ contact.full_name }}</span>
                                        </span>
                                    </el-option>
                                </el-select>
                            </div>
                        </div>
                    </div>

                    <div class="form-group">
                        <el-checkbox v-model="bulkSetContactForm.remove_all_other">Remove existing contacts in this role</el-checkbox>
                    </div>
                </div>

                <span slot="footer" class="dialog-footer">
                    <button class="btn btn-default waves-effect" @click.prevent="closeSetPrimaryContactModal">Cancel</button>
                    <button class="btn btn-success waves-effect waves-light" @click.prevent="setPrimaryContacts"
                            :disabled="loading">{{ loading ? "Setting Contacts..." : "Confirm" }}
                    </button>
                </span>
            </el-dialog>
        </div>

        <simple-modal :show="showChangeAssetsGroupsBulkUpdateModal" @close="toggleAssetGroupsBulkUpdateModal()"
                      title="Select Asset Group" :disable-close="bulkUpdating">
            <div class="flex flex-col items-end gap-4">
                <remote-select
                    v-model="selectedAssetGroupForBulkUpdate"
                    @focus="searchAssetGroups"
                    :remote-method="searchAssetGroups"
                    :data="assetGroups"
                    :loading="loading"
                    class="w-full"
                    item-label="name"
                    placeholder="Search Asset Groups">
                    <template slot-scope="props">
                        {{ props.item.name }}
                    </template>
                </remote-select>

                <secondary-button @click.prevent="moveSelectedAssetsToAssetGroup()" :disabled="bulkUpdating">
                    Change To Asset Group
                </secondary-button>
            </div>
        </simple-modal>

        <simple-modal :show="showTodoModal"
                      @close="toggleTodoModal()"
                      :title="todoModalTitle">
            <todo-create-edit-form @close="toggleTodoModal"
                                   :todo="selectedTodo"
                                   @todo-updated="doFilter"
                                   @todo-created="doFilter()">
            </todo-create-edit-form>
        </simple-modal>
    </div>
</template>

<script>
import AssetGroupSearch from "../../mixins/asset-group-search";
import vueTableMixin from "../../mixins/vuetable-css-mixin";
import GroupToggleSelect from "../library/GroupToggleSelect.vue";
import ExportFileMixin from "../../mixins/export-file";

export default {
    name: "AssetsList",
    components: {GroupToggleSelect},

    mixins: [vueTableMixin, AssetGroupSearch, ExportFileMixin],

    props: {
        hasQueuedImports: {
            type: Boolean,
            required: false,
            default: false
        },
        inspectionTypes: {
            required: true,
            type: Array,
        },
    },

    created () {
        this.searchAssetGroups();
        let self = this;
        this.getPaypropFilesIntegration();
        Bus.$on("asset-created", function () {
            self.hideAssetForm();
            Vue.nextTick(() => self.$refs.vuetable.refresh());
        });
    },

    data () {
        return {
            provinces: ["Eastern Cape", "Free State", "Gauteng", "KwaZulu-Natal", "Limpopo", "Mpumalanga", "Northern Cape", "North West", "Western Cape"],
            loading: false,
            isLoadingTableData: false,
            searchParams: {
                filter_text: "",
                archived_filter: false,
                portfolio_ids_filter: null,
                mandate_type_filter: null,
                last_inspection_date_filter: "all",
                last_ticket_date_filter: "all",
                last_maintenance_request_date_filter: "all",
                next_inspection_date_filter: "all",
                reminder_date_filter: "all",
                asset_group_filter: null,
                inspection_type_filter: null,
                todo_date_range_filter: null,
            },
            showTodoModal: false,
            selectedTodo: null,
            mandateTypes: ["unspecified", "managed", "unmanaged"],
            showBulkActions: false,
            showMergeOption: false,
            showPayPropUploadForm: false,
            showChangeAssetsGroupsBulkUpdateModal: false,
            bulkUpdating: false,
            selectedAssetGroupForBulkUpdate: null,
            setPrimaryContactModalVisible: false,
            hasPaypropFilesIntegration: false,
            selectedAsset: {},
            showInspectionFilters: false,
            showMaintenanceFilters: false,
            columns: [
                {
                    name: "__checkbox",
                    titleClass: "center aligned",
                    dataClass: "center aligned column-checkbox"
                },
                {
                    name: "__slot:asset_name",
                    sortField: "name",
                    title: "Name",
                    dataClass: "text-nowrap column-width-250 column-width-250-fixed"
                },
                {
                    name: "full_address",
                    title: "Address",
                    dataClass: "text-nowrap column-width-200"
                },
                {
                    name: "__slot:landlord_full_name",
                    title: "Landlord",
                    dataClass: "text-nowrap"
                },
                {
                    name: "tenant.full_name",
                    title: "Tenant",
                    dataClass: "text-nowrap"
                },
                {
                    name: "__slot:last_inspection",
                    title: "Last Inspection",
                },
                {
                    name: "__slot:next_scheduled_inspection",
                    title: "Next Scheduled Inspection",
                },
                {
                    name: "__slot:next_todo",
                    title: "Next To-do",
                    callback: "formatDate",
                },
                {
                    name: "__slot:view_asset",
                    title: "",
                    titleClass: "center aligned",
                    dataClass: "text-right"
                }
            ],
            importForm: new AppForm({}),
            assetImportUploadRequestHeaders: window.axios.defaults.headers.common,
            suggestedContacts: [],
            selectedContact: null,
            bulkSetContactForm: new AppForm({})
        };
    },

    computed: {
        portfolioFilterOptions () {
            return _.sortBy(this.state.user.portfolios, "name");
        },

        currentMandateFilter () {
            return this.searchParams.mandate_type_filter ? this.searchParams.mandate_type_filter : "All Mandates";
        },

        assetImportUploadUrl () {
            return "/assets/import";
        },

        todoModalTitle () {
            return this.selectedTodo ? "Edit To-do" : "Create To-do";
        },

        lastInspectionFilterLabel () {
            switch (this.searchParams.last_inspection_date_filter) {
                case "all":
                    return "Last Inspection";

                case "mt_3_months_ago":
                    return "More than 3 months ago";

                case "mt_6_months_ago":
                    return "More than 6 months ago";

                case "mt_12_months_ago":
                    return "More than 12 months ago";

                case "never":
                    return "Never";
            }

            return "Last Inspection";
        },

        lastMaintenanceTicketFilter () {
            switch (this.searchParams.last_ticket_date_filter) {
                case "all":
                    return "All";

                case "mt_3_months_ago":
                    return "More than 3 months ago";

                case "mt_6_months_ago":
                    return "More than 6 months ago";

                case "mt_12_months_ago":
                    return "More than 12 months ago";

                case "never":
                    return "Never";
            }

            return "All";
        },

        lastMaintenanceRequestFilter () {
            switch (this.searchParams.last_maintenance_request_date_filter) {
                case "all":
                    return "All";

                case "mt_3_months_ago":
                    return "More than 3 months ago";

                case "mt_6_months_ago":
                    return "More than 6 months ago";

                case "mt_12_months_ago":
                    return "More than 12 months ago";

                case "never":
                    return "Never";
            }

            return "All";
        },

        nextInspectionFilterLabel () {
            switch (this.searchParams.next_inspection_date_filter) {
                case "all":
                    return "Next Inspection";

                case "lt_3_months":
                    return "In Less than 3 months";

                case "lt_6_months":
                    return "In Less than 6 months";

                case "lt_12_months":
                    return "In Less than 12 months";

                case "never":
                    return "Never";
            }

            return "Next Inspection";
        },

        reminderDateFilterLabel () {
            // current, upcoming, all, none
            if (this.searchParams.reminder_date_filter === "none") {
                return "No To-do Set";
            }
            return _.capitalize(this.searchParams.reminder_date_filter) + " To-dos";
        },

        perPageLabel () {
            let perPage = this.searchParams.per_page;
            if (!perPage) {
                perPage = 10;
            }
            return perPage + " per page";
        },
    },

    methods: {
        initialiseBulkSetContactForm () {
            this.selectedContact = null;
            this.bulkSetContactForm = new AppForm({
                remove_all_other: false,
                selected_contact: null,
                role: "landlord",
                assets: this.$refs.vuetable.selectedTo
            });
        },

        showSetPrimaryContactModal () {
            this.initialiseBulkSetContactForm();
            this.setPrimaryContactModalVisible = true;
        },

        closeSetPrimaryContactModal () {
            this.setPrimaryContactModalVisible = false;
        },

        setPrimaryContacts () {
            this.loading = true;
            this.bulkSetContactForm.selected_contact = this.selectedContact.id;

            axios.post("/assets/bulk/set-primary-contacts", this.bulkSetContactForm)
            .then(() => {
                this.closeSetPrimaryContactModal();
                toastr.success("Assets updated!");
                this.loading = false;
                this.refreshPage();
            }).catch((errors) => {
                toastr.error("Could not set primary " + this.bulkSetContactForm.role + " contacts. " + errors);
            });
        },

        searchContacts (query) {
            axios.get("/contacts/search-to-link", {
                params: {
                    filter_text: query
                }
            }).then((response) => {
                this.suggestedContacts = response.data;
            }).catch((errors) => {
                toastr.error("Could not find any matching contacts!");
                this.loading = false;
            });
        },

        addressSpan (value) {
            return "<span>" + value + "</span>";
        },

        showAssetForm () {
            if (!this.verifyTeamIsSubscribed()) {
                return;
            }

            if (this.app.state.assets_remaining <= 0) {
                swal({
                    title: "Property Limit Reached!",
                    text: "Consider upgrading to Enterprise Account for unlimited assets!",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#3085d6",
                    cancelButtonColor: "#d33",
                    confirmButtonText: "Yes, Let's upgrade!",
                    cancelButtonText: "Remind me later"
                }).then((result) => {
                    if (result.value) {
                        window.location = "/settings#/tab-billing";
                    }
                });
            } else {
                $("#modal-asset-form").modal("show");
            }
        },

        hideAssetForm () {
            $("#modal-asset-form").modal("hide");
        },

        showMergeModal () {
            $("#merge-property-modal").modal("show");
        },

        hideMergeModal () {
            $("#merge-property-modal").modal("hide");
        },

        toggleMergeOption () {
            this.showMergeOption = !this.showMergeOption;
        },

        onActionClick (action, data, index) {
            switch (action) {
                case "view-item":
                    window.location = "/assets/" + data.id;
                    break;
                case "merge-asset":
                    this.selectedAsset = data;
                    this.showMergeModal();
            }
        },

        selectMandateTypeFilter (mandateType) {
            this.$set(this.searchParams, "mandate_type_filter", mandateType);
            this.doFilter();
        },

        onPaginationData (paginationData) {
            this.$refs.pagination.setPaginationData(paginationData);
            this.$refs.paginationInfo.setPaginationData(paginationData);
        },

        onChangePage (page) {
            this.$refs.vuetable.changePage(page);
        },

        doFilter: _.debounce(function () {
            this.hideInspectionFilters();
            this.hideMaintenanceFilters();
            Vue.nextTick(() => this.$refs.vuetable.refresh());
        }, 500),

        checkboxToggled () {
            this.showBulkActions = this.$refs.vuetable.selectedTo.length > 0;
        },

        editTodo (todo, asset) {
            todo.asset = asset;
            this.selectedTodo = todo;
            this.showTodoModal = true;
        },

        updatePortfolio () {
            let portfolios = {};
            this.app.state.user.portfolios.forEach((p) => {
                portfolios[p.id] = p.name;
            });

            let assetName = this.$refs.vuetable.selectedTo.length > 1 ? this.app.lang.assetsName : this.app.lang.assetName;

            let self = this;
            swal({
                title: "Change Portfolio for " + this.$refs.vuetable.selectedTo.length + " " + assetName,
                text: "Select the portfolio to move the " + assetName + " to.",
                input: "select",
                inputOptions: portfolios,
                showCancelButton: true,
                inputValidator: (value) => {
                    return !value && "Select a portfolio";
                }
            }).then((result) => {
                if (result.value) {
                    self.moveSelectedAssetsToPortfolio(result.value);
                }
            });
        },

        archiveAssets () {
            let assetName = this.$refs.vuetable.selectedTo.length > 1 ? this.app.lang.assetsName : this.app.lang.assetName;
            let message = this.$refs.vuetable.selectedTo.length > 1 ?
                "Are you sure you want to archive all the selected " + assetName : "Are you sure you want to archive the selected " + assetName;

            let self = this;
            swal({
                title: "Archive " + this.$refs.vuetable.selectedTo.length + " " + assetName,
                text: message,
                showCancelButton: true
            }).then((result) => {
                if (result.value) {
                    self.archiveSelectedAssets(result.value);
                }
            });
        },

        archiveSelectedAssets () {
            let message = this.$refs.vuetable.selectedTo.length > 1 ? this.app.lang.assetsName : this.app.lang.assetName;
            message += " archived!";

            App.post("/assets/bulk/archive", new AppForm({
                assets: this.$refs.vuetable.selectedTo
            })).then(() => {
                toastr.success(message);
                this.reloadTable();
            }).catch(error => {
                toastr.error(error.message);
            });
        },

        unarchiveSelectedAssets () {
            let message = this.$refs.vuetable.selectedTo.length > 1 ? this.app.lang.assetsName : this.app.lang.assetName;
            message += " unarchived!";
            App.post("/assets/bulk/unarchive", new AppForm({
                assets: this.$refs.vuetable.selectedTo
            })).then(() => {
                toastr.success(message);
                this.reloadTable();
            }).catch(error => {
                toastr.error(error.message);
            });
        },

        reloadTable () {
            Vue.nextTick(() => this.$refs.vuetable.refresh());
            this.$refs.vuetable.selectedTo = [];
            this.showBulkActions = false;
        },

        refreshPage () {
            Vue.nextTick(() => this.$refs.vuetable.reload());
            this.$refs.vuetable.selectedTo = [];
            this.showBulkActions = false;
        },

        moveSelectedAssetsToPortfolio (portfolioId) {
            App.post("/assets/bulk/portfolios/update", new AppForm({
                asset_ids: this.$refs.vuetable.selectedTo,
                portfolio_id: portfolioId
            })).then(response => {
                toastr.success(response);
                this.reloadTable();
            })
            .catch(error => {
                toastr.error("Error updating portfolios. Error: " + error.message);
            });
        },

        toggleAssetGroupsBulkUpdateModal () {
            this.showChangeAssetsGroupsBulkUpdateModal = !this.showChangeAssetsGroupsBulkUpdateModal;
        },

        moveSelectedAssetsToAssetGroup () {
            this.bulkUpdating = true;
            App.post("/assets/bulk/groups/update", new AppForm({
                assets: this.$refs.vuetable.selectedTo,
                asset_group: this.selectedAssetGroupForBulkUpdate ? this.selectedAssetGroupForBulkUpdate.id : null
            })).then((response) => {
                toastr.success(response);
                this.reloadTable();
                this.toggleAssetGroupsBulkUpdateModal();
                this.bulkUpdating = false;
            }).catch((error) => {
                this.bulkUpdating = false;
                this.toggleAssetGroupsBulkUpdateModal();
                toastr.error(error.message);
            });
        },

        toggleArchivedAssets () {
            this.$set(this.searchParams, "archived_filter", !this.searchParams.archived_filter);
            this.doFilter();
        },

        showImportFileUploadForm () {
            $("#asset-import-modal").modal("show");
        },

        handleImportUploadError (error, file, fileList) {
            toastr.error("File upload failed! Please try again.");
        },

        handleImportUploadSuccess (res, file) {
            toastr.success(res);
        },

        exportAssets () {
            this.$set(this.searchParams, "sort", this.$refs.vuetable?.getSortParam());
            this.exportData("/assets/export", this.searchParams, this.$dayjs().format("DD-MM-YYYY") + " Assets Export.xlsx");
        },

        getPaypropFilesIntegration () {
            axios.get("/integrations/payprop/check-status")
            .then(response => {
                this.hasPaypropFilesIntegration = response.data.hasIntegration;
            })
            .catch(error => {
                toastr.error(`Unable to fetch PayProp integration status. Error: ${error.message}`);
            });
        },

        toggleTodoModal () {
            this.showTodoModal = !this.showTodoModal;
            if (!this.showTodoModal) {
                this.selectedTodo = null;
            }
        },

        toggleInspectionFilters () {
            this.showInspectionFilters = !this.showInspectionFilters;
        },

        toggleMaintenanceFilters () {
            this.showMaintenanceFilters = !this.showMaintenanceFilters;
        },

        hideInspectionFilters () {
            this.showInspectionFilters = false;
        },

        hideMaintenanceFilters () {
            this.showMaintenanceFilters = false;
        },

        filterReminderDate (filter) {
            this.$set(this.searchParams, "reminder_date_filter", filter);
        },

        filterLastInspectionDate (filter) {
            this.$set(this.searchParams, "last_inspection_date_filter", filter);
        },

        filterLastMaintenanceTicketDate (filter) {
            this.$set(this.searchParams, "last_ticket_date_filter", filter);
        },

        filterLastMaintenanceRequestDate (filter) {
            this.$set(this.searchParams, "last_maintenance_request_date_filter", filter);
        },

        filterNextInspectionDate (filter) {
            this.$set(this.searchParams, "next_inspection_date_filter", filter);
        },

        setAssetGroupFilter (id) {
            this.$set(this.searchParams, "asset_group_filter", id);
            this.doFilter();
        },

        filterPerPage (perPage) {
            this.$set(this.searchParams, "per_page", perPage);
            this.doFilter();
        },

        onLoading () {
            this.isLoadingTableData = true;
        },

        onLoaded () {
            this.isLoadingTableData = false;
        },
    },
};
</script>





